import React, { useState, useEffect, useCallback } from "react";
import { FaEllipsisH, FaSearch } from "react-icons/fa";
import InformationModal from "../../../components/informationModal";
import FeedbackModall from "../../../components/FeedbackModall";
import * as localService from "../../../services/localService";
import apiService from "../../../services/apiService";
import { organizationApiService } from "../../../services/apiV2Service";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { formatDateTime } from "../../../utils/dateUtils";
import { Modal } from "react-bootstrap";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import Pagination from "../../../components/Pagination";
import TablePageLayout from "../../../components/TablePageLayout";
import EditOrganizationModal from "../../host-experience/host-clients/components/modals/EditOrganizationModal";
import { useHostOrganizationEdit } from "../hooks/useHostOrganizationEdit";

const ApprovedHosts = () => {
  const [approvedHosts, setApprovedHosts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  // Accept Changes Modal
  const [showModal, setShowModal] = useState(false);
  const [showDisableHostModal, setShowDisableHostModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1); // Set to 1 as ResponsiveTable expects 1-based
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Edit functionality using shared hook
  const {
    showEditModal,
    selectedOrganization,
    editFormData,
    editLoading,
    editError,
    fetchingData,
    commissionPercentage,
    setCommissionPercentage,
    isCustomRevenueShare,
    defaultCommission,
    handleEditShow,
    handleEditClose,
    handleInputChange,
    handleSubmit,
    handleDeleteRevenueShare,
  } = useHostOrganizationEdit();

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  // Decline Changes Modal
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const openDeclineModal = () => setShowDeclineModal(true);
  const closeDeclineModal = () => setShowDeclineModal(false);

  // Show Feedback M
  const [showFeedbackModal, setshowFeedbackModal] = useState(false);

  const openDeclineModalFeedback = () => {
    setShowDeclineModal(false);
    setshowFeedbackModal(true);
  };

  const [disableReason, setDisableReason] = useState("");
  const [showDisableReasonModal, setShowDisableReasonModal] = useState(false);

  const unapproveSelectedHosts = useCallback(() => {
    setShowModal(false);
    const companyIds = approvedHosts.filter(o => o.checked === true).map(o => o.companyId);
    const currentUser = localService.getUser();
    const id = currentUser && currentUser.id;

    apiService
      .updateCompanyStatus(companyIds.join(","), id, false)
      .then(res => {
        if (res.status === 200) {
          showApiMessage(res, "unapprove hosts");
          window.location.reload(false);
        }
      })
      .catch(err => {
        showApiMessage(err, "unapprove hosts");
      })
      .finally(() => {
        setShowModal(false);
      });
  });

  const disableHost = companyId => {
    setShowDisableHostModal(false);
    const currentUser = localService.getUser();
    const id = currentUser && currentUser.id;

    apiService
      .updateCompanyStatus(companyId, id, false, disableReason)
      .then(res => {
        if (res.status === 200) {
          setShowDisableHostModal(false);
          showApiMessage(res, "disable host");
          window.location.reload(false);
        }
      })
      .catch(err => {
        showApiMessage(err, "disable host");
      })
      .finally(() => {
        setShowDisableHostModal(false);
      });
  };

  const displayDisableHostModal = (show, companyId) => {
    if (show) {
      setSelectedCompany(companyId);
      displayDisableReasonModal(); // Show the reason modal first
    } else {
      setShowDisableHostModal(false);
    }
  };

  const closeFeedbackDeclineModal = () => setshowFeedbackModal(false);

  useEffect(() => {
    loadOrganizationsByStatus(currentPage, searchValue);
  }, []);

  // Auto-search after 3 characters
  useEffect(() => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setCurrentPage(1);
      loadOrganizationsByStatus(1, searchValue);
    }
  }, [searchValue]);

  const loadOrganizationsByStatus = async (page = currentPage, search = searchValue) => {
    setLoading(true);
    try {
      // Get ACTIVE organizations (approved) for both HOST_PROPERTY_DISTRIBUTION and HOST_PROPERTY_MANAGEMENT types
      const distributionParams = {
        typeId: 2, // HOST_PROPERTY_DISTRIBUTION
        status: "ACTIVE",
        page: page,
        limit: 10,
        ...(search && { q: search }), // Add q param if search exists
      };

      const managementParams = {
        typeId: 3, // HOST_PROPERTY_MANAGEMENT
        status: "ACTIVE",
        page: page,
        limit: 10,
        ...(search && { q: search }), // Add q param if search exists
      };

      const [distributionResponse, managementResponse] = await Promise.all([
        organizationApiService.getOrganizations(distributionParams),
        organizationApiService.getOrganizations(managementParams),
      ]);

      const distributionOrgs = distributionResponse.data?.data || [];
      const managementOrgs = managementResponse.data?.data || [];
      const allOrganizations = [...distributionOrgs, ...managementOrgs];

      const pagination = distributionResponse.data?.pagination || {};

      setTotalPageSize(pagination.totalCount || allOrganizations.length);
      setItemsPerPage(pagination.limit || 10);
      setApprovedHosts(allOrganizations);
    } catch (err) {
      logger.error("Error loading active organizations:", err);
      showApiMessage(err, "fetch active organizations");
    } finally {
      setLoading(false);
    }
  };

  const changePage = selectedObj => {
    const pageNumber = Number(selectedObj.selected) || 1;
    setCurrentPage(pageNumber);
    loadOrganizationsByStatus(pageNumber, searchValue);
  };

  const performSearch = async e => {
    if (e && e.preventDefault) e.preventDefault();
    // Reset to first page when searching
    setCurrentPage(1);
    setLoading(true);

    try {
      // For now, we'll use the same loadOrganizationsByStatus function
      // Later this can be enhanced to include search functionality
      await loadOrganizationsByStatus(1, searchValue);
    } catch (err) {
      logger.error("Error searching hosts:", err);
      showApiMessage(err, "search hosts");
    } finally {
      setLoading(false);
    }
  };

  // Add a function to handle the modal hide
  const handleStuffOnModalHide = () => {
    setShowDisableReasonModal(false);

    const approvedHostsContainer = document.querySelector(".approved-hosts");
    if (approvedHostsContainer != null) {
      approvedHostsContainer.classList.remove("blur-background");
    }
  };

  // Add a function to display the disable reason modal
  const displayDisableReasonModal = () => {
    setShowDisableReasonModal(true);

    const approvedHostsContainer = document.querySelector(".approved-hosts");
    if (approvedHostsContainer != null) {
      approvedHostsContainer.classList.add("blur-background");
    }
  };

  const handleUnapprove = async (hostId) => {
    try {
      await organizationApiService.updateApprovedStatus(hostId, { approved: false });
      showApiMessage({ message: "Organization unapproved and hidden successfully" }, "success");
      loadOrganizationsByStatus();
    } catch (err) {
      logger.error("Error unapproving organization:", err);
      showApiMessage(err, "unapprove organization");
    }
  };

  return (
    <>
      <TablePageLayout
        title="Active Hosts"
        subtitle="Manage and view your active host listings"
        breadcrumbs={[{ label: "Home", path: "/host" }, { label: "Active Hosts" }]}
        showSearch={true}
        searchValue={searchValue}
        onSearchChange={e => setSearchValue(e.target.value)}
        onSearchSubmit={performSearch}
        searchPlaceholder="Search by company name, email, phone, or location..."
        loading={loading}
        emptyMessage="No active hosts found"
        showPagination={Array.isArray(approvedHosts) && approvedHosts.length > 0 && totalPageSize > 0}
        currentPage={currentPage}
        totalCount={totalPageSize}
        pageSize={itemsPerPage}
        onPageChange={changePage}
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-12 font-semibold">#</TableHead>
                <TableHead className="font-semibold">Company</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Organization Type</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Date Approved</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedHosts.map((host, index) => (
                <TableRow key={host.id} className="hover:bg-muted/50">
                  <TableCell className="text-gray-500 font-medium">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{host.name}</span>
                      <span className="text-sm text-gray-600">{host.address || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm">{host.contactEmail || "N/A"}</span>
                      <span className="text-sm text-gray-600">{host.contactMsisdn || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {host.typeId === 2
                        ? "Host Property Distribution"
                        : host.typeId === 3
                          ? "Host Property Management"
                          : "Unknown Type"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
                        {host.status || "ACTIVE"}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={
                          host.approved !== false
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }
                      >
                        {host.approved !== false ? "Approved" : "Unapproved"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{formatDateTime(host.createdAt).date}</div>
                      <div className="text-xs text-muted-foreground">{formatDateTime(host.createdAt).time}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2 items-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-600 border-amber-600 hover:bg-amber-50"
                        onClick={() => handleUnapprove(host.id)}
                      >
                        Unapprove
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditShow(host)} title="Edit Host">
                        <FaEllipsisH className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TablePageLayout>

      {/* Disable Reason Modal */}
      <Modal
        show={showDisableReasonModal}
        onHide={handleStuffOnModalHide}
        centered
        className="rounded-lg overflow-hidden"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">Reason For Disabling</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter reason for disabling this host"
            rows="4"
            onChange={e => setDisableReason(e.target.value)}
            style={{ resize: "none" }}
          />
          <div className="flex justify-center mt-6">
            <button
              className="px-4 py-2 border border-transparent rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={!disableReason}
              onClick={() => {
                setShowDisableReasonModal(false);
                setShowDisableHostModal(true);
              }}
            >
              Submit
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Confirmation Modals - keep these for now as they're part of an external component */}
      <InformationModal
        body="Are you sure you want to unapprove host?"
        title="Confirm Unapprove Host"
        isOpen={showModal}
        isClose={closeModal}
        onSubmit={unapproveSelectedHosts}
        acceptButton="Unapprove"
        declineButton="Cancel"
      >
        <div style={{ fontWeight: 400, fontSize: "1.2rem" }}>Are you sure you want to unapprove host?</div>
      </InformationModal>

      <InformationModal
        body="Are you sure you want to disable host?"
        title="Confirm Disable Host"
        isOpen={showDisableHostModal}
        isClose={() => setShowDisableHostModal(false)}
        onSubmit={() => disableHost(selectedCompany)}
        acceptButton="Disable"
        declineButton="Cancel"
      >
        <div style={{ fontWeight: 400, fontSize: "1.2rem" }}>Are you sure you want to disable host?</div>
      </InformationModal>

      <FeedbackModall
        body="Your Changes have been implemented"
        title="Done"
        isOpen={showFeedbackModal}
        isClose={closeFeedbackDeclineModal}
        onSubmit={closeFeedbackDeclineModal}
        acceptButton="Okay"
        doneButton="Okay"
        declineButton="Cancel"
      >
        <img src="assets/img/Web/Spacia/check.png" alt="check" />
        <div style={{ paddingTop: 10, fontWeight: 400 }}>Your Changes have been declined</div>
      </FeedbackModall>

      {/* Edit Host Dialog */}
      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        selectedOrganization={selectedOrganization}
        editOrganizationForm={editFormData}
        editOrganizationLoading={editLoading}
        editOrganizationError={editError}
        fetchingOrganizationData={fetchingData}
        onInputChange={handleInputChange}
        onSubmit={async () => {
          const result = await handleSubmit();
          if (result?.success) {
            loadOrganizationsByStatus();
          }
        }}
        isHostMode={true}
        commissionPercentage={commissionPercentage}
        onCommissionChange={setCommissionPercentage}
        defaultCommission={defaultCommission}
        isCustomRevenueShare={isCustomRevenueShare}
        onDeleteRevenueShare={handleDeleteRevenueShare}
      />
    </>
  );
};

export default ApprovedHosts;
